import React from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

export const groupedByMonth = ({ rows, months, serviceMonth, renderRow, keyPrefix }) => {
  const monthsInRows = months.filter((month) => rows.some((row) => row.serviceMonth === month));
  if (monthsInRows.length <= 1) return rows.map((row, index) => renderRow(row, `${keyPrefix}-${index}`));
  return monthsInRows.map((month) => {
    const monthRows = rows.filter((row) => row.serviceMonth === month);
    return (
      <Box key={`${keyPrefix}-${month}`}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', pl: 0.5, pt: 0.5, pb: 0.25 }}
        >
          {month}{month === serviceMonth ? ' - current' : ''}
        </Typography>
        <Stack spacing={0.5}>
          {monthRows.map((row, index) => renderRow(row, `${keyPrefix}-${month}-${index}`))}
        </Stack>
      </Box>
    );
  });
};

const parseEvidenceTimestamp = (value) => {
  if (!value) return 0;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  const text = String(value).trim();
  if (!text) return 0;
  const usDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})(?:\s|$)/);
  if (usDate) {
    const year = usDate[3].length === 2 ? Number(`20${usDate[3]}`) : Number(usDate[3]);
    const parsed = new Date(year, Number(usDate[1]) - 1, Number(usDate[2]));
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const evidenceTimestamp = (item = {}) => parseEvidenceTimestamp(
  item.effectiveDate
  || item.cancellationDate
  || item.orderEndDate
  || item.activationDate
  || item.orderStartDate
  || item.enrollmentDate
  || item.lastModified
  || item.updatedAt
  || item.lastUpdated,
);

const PRODUCT_SUFFIX_RANK_ENTRIES = [
  ['', 0],
  ['C', 0],
  ['G', 10],
  ['B', 11],
  ['BG', 12],
  ['GB', 12],
  ['CG', 13],
  ['GC', 13],
  ['CB', 14],
  ['BC', 14],
  ['CBG', 20],
  ['BCG', 20],
  ['CGB', 20],
  ['BGC', 20],
  ['GCB', 20],
  ['GBC', 20],
  ['C_BG', 20],
  ['CAD', 30],
  ['K', 40],
];

const PRODUCT_SUFFIX_RANK = new Map(PRODUCT_SUFFIX_RANK_ENTRIES);
const PRODUCT_SUFFIXES = PRODUCT_SUFFIX_RANK_ENTRIES
  .map(([suffix]) => suffix)
  .filter(Boolean)
  .sort((left, right) => right.length - left.length);

const normalizedCodeWithoutSecondary = (value) => String(value || '')
  .trim()
  .toUpperCase()
  .replace(/_+$/, '')
  .replace(/_SEC$/i, '')
  .replace(/_+$/, '');

const productSuffixKey = (code) => {
  const normalized = normalizedCodeWithoutSecondary(code);
  if (!normalized) return '';
  if (normalized.split('_').filter(Boolean).length <= 2) return '';
  return PRODUCT_SUFFIXES.find((suffix) => normalized.endsWith(`_${suffix}`)) || '';
};

const codeStem = (value) => {
  const normalized = normalizedCodeWithoutSecondary(value);
  const suffix = productSuffixKey(normalized);
  return suffix ? normalized.slice(0, -(suffix.length + 1)) : normalized;
};

const vehicleSyndicationExportFamily = (value) => {
  const compact = String(value || '').replace(/_/g, '');
  return ['DIVSSE', 'DIVSSSE', 'DIVSSUE'].includes(compact) ? 'DI_VSSE' : null;
};

export const firstDisplayDate = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || '';

export const formatEvidenceDate = (value) => {
  if (value === null || value === undefined || value === '') return '';
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return '';
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const year = String(value.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  }
  const text = String(value).trim();
  const isoDate = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) return `${isoDate[2]}/${isoDate[3]}/${isoDate[1].slice(-2)}`;
  const usDate = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})(?:\s|$)/);
  if (usDate) {
    const month = usDate[1].padStart(2, '0');
    const day = usDate[2].padStart(2, '0');
    const year = usDate[3].slice(-2);
    return `${month}/${day}/${year}`;
  }
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const year = String(parsed.getFullYear()).slice(-2);
  return `${month}/${day}/${year}`;
};

export const productCardDate = (item = {}, status = item.productStatus || item.status) => {
  if (String(status || '').toUpperCase().includes('CANCEL')) {
    return firstDisplayDate(
      item.cancellationDate,
      item.cancelDate,
      item.billingEndDate,
      item.orderEndDate,
      item.effectiveDate,
      item.activationDate,
      item.goLiveDate,
      item.orderStartDate,
      item.productSelectionDate,
      item.enrollmentDate,
      item.lastModified,
    );
  }
  return firstDisplayDate(
    item.effectiveDate,
    item.activationDate,
    item.goLiveDate,
    item.billingStartDate,
    item.orderStartDate,
    item.productSelectionDate,
    item.enrollmentDate,
    item.lastModified,
  );
};

export const productCardMeta = ({ code, date }) => [
  code,
  formatEvidenceDate(date),
];

export const productFamilyKey = (code) => {
  const normalized = normalizedCodeWithoutSecondary(code);
  if (!normalized) return 'UNCODED';
  const withoutBrand = codeStem(normalized);
  const exportFamily = vehicleSyndicationExportFamily(withoutBrand);
  if (exportFamily) return exportFamily;
  return withoutBrand || normalized;
};

const productSuffixRank = (code) => PRODUCT_SUFFIX_RANK.get(productSuffixKey(code)) ?? 90;

export const compareProductCodesForLane = (leftCode, rightCode) => {
  const leftFamily = productFamilyKey(leftCode);
  const rightFamily = productFamilyKey(rightCode);
  const familySort = leftFamily.localeCompare(rightFamily, undefined, { numeric: true, sensitivity: 'base' });
  if (familySort !== 0) return familySort;
  const suffixSort = productSuffixRank(leftCode) - productSuffixRank(rightCode);
  if (suffixSort !== 0) return suffixSort;
  const exactCodeSort = String(leftCode || '').localeCompare(String(rightCode || ''), undefined, { numeric: true, sensitivity: 'base' });
  return exactCodeSort;
};

const compareEvidenceProductsByLane = (left = {}, right = {}, codeForRow = (row) => row.productCode || row.oemProductCode) => {
  const leftCode = String(codeForRow(left) || '').trim();
  const rightCode = String(codeForRow(right) || '').trim();
  if (leftCode || rightCode) {
    if (!leftCode) return 1;
    if (!rightCode) return -1;
    const laneSort = compareProductCodesForLane(leftCode, rightCode);
    if (laneSort !== 0) return laneSort;
  }
  const dateSort = evidenceTimestamp(right) - evidenceTimestamp(left);
  if (dateSort !== 0) return dateSort;
  return String(left.productName || left.productSelection || '').localeCompare(String(right.productName || right.productSelection || ''), undefined, { sensitivity: 'base' });
};

export const productFamilyLabel = (key) => (key === 'UNCODED' ? 'Uncoded products' : key);

export const groupRowsByProductFamily = (rows = [], codeForRow = (row) => row.productCode || row.oemProductCode, familyOrder = []) => {
  const groups = new Map();
  rows.forEach((row) => {
    const key = productFamilyKey(codeForRow(row));
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  const orderedKeys = [...new Set([
    ...familyOrder.map(productFamilyKey),
    ...groups.keys(),
  ])].filter(Boolean);
  return orderedKeys
    .map((key) => ({
      key,
      rows: [...(groups.get(key) || [])].sort((left, right) => compareEvidenceProductsByLane(left, right, codeForRow)),
    }));
};

const EmptyProductFamilySlot = ({ label, dense = false }) => (
  <Paper
    variant="outlined"
    data-billing-product-family-placeholder="true"
    sx={{
      minHeight: dense ? 43 : 56,
      borderRadius: 1,
      borderStyle: 'dashed',
      borderColor: 'divider',
      bgcolor: 'action.hover',
      opacity: 0.5,
      display: 'flex',
      alignItems: 'center',
      px: dense ? 1 : 1.25,
    }}
  >
    <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 800 }}>
      {label}
    </Typography>
  </Paper>
);

export const renderProductFamilyLanes = ({
  rows,
  codeForRow,
  renderRow,
  keyPrefix,
  familyOrder = [],
  familyMinRows = {},
  showEmptyLanes = false,
  emptyLaneLabel = 'No product',
  dense = false,
}) => {
  const groups = groupRowsByProductFamily(rows, codeForRow, familyOrder);
  if (!showEmptyLanes && groups.length <= 1) return rows.map((row, index) => renderRow(row, `${keyPrefix}-${index}`));
  return groups.map((group) => (
    <Box
      key={`${keyPrefix}-${group.key}`}
      data-billing-product-family={group.key}
      data-billing-product-family-empty={group.rows.length === 0 ? 'true' : undefined}
      sx={{ minHeight: dense ? 82 : 95 }}
    >
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 800, display: 'block', pl: 0.5, pt: 0.5, pb: 0.25, opacity: group.rows.length ? 1 : 0.5 }}
      >
        {productFamilyLabel(group.key)}
        {group.rows.length > 1 ? ` - ${group.rows.length} rows` : ''}
      </Typography>
      <Stack spacing={0.5}>
        {group.rows.map((row, index) => renderRow(row, `${keyPrefix}-${group.key}-${index}`))}
        {Array.from({ length: showEmptyLanes ? Math.max(0, (Number(familyMinRows[group.key]) || 0) - group.rows.length) : 0 }).map((_, index) => (
          <EmptyProductFamilySlot
            key={`${keyPrefix}-${group.key}-empty-${index}`}
            label={emptyLaneLabel}
            dense={dense}
          />
        ))}
      </Stack>
    </Box>
  ));
};

export const buildDateLookupByProductCode = (items = []) => items.reduce((lookup, item) => {
  const date = firstDisplayDate(item.effectiveDate, item.cancellationDate, item.orderEndDate, item.activationDate, item.orderStartDate, item.enrollmentDate);
  const code = String(item.productCode || item.oemProductCode || '').trim();
  if (!code || !date) return lookup;
  const key = code.toUpperCase();
  const stem = codeStem(code);
  const current = lookup.get(key);
  if (!current || parseEvidenceTimestamp(date) > parseEvidenceTimestamp(current)) lookup.set(key, date);
  if (stem) {
    const currentStem = lookup.get(stem);
    if (!currentStem || parseEvidenceTimestamp(date) > parseEvidenceTimestamp(currentStem)) lookup.set(stem, date);
  }
  return lookup;
}, new Map());

export const inheritedDateForCode = (lookup, code) => {
  const key = String(code || '').trim().toUpperCase();
  if (!key) return '';
  return lookup.get(key) || lookup.get(codeStem(key)) || '';
};

export const sortEvidenceProducts = (items = []) => [...items].sort(compareEvidenceProductsByLane);

export const draftAddContext = ({ fileKind, sourceLabel, draft }) => ({
  source: fileKind,
  sourceLabel,
  productName: draft.productName,
  productCode: draft.productCode,
  price: Number(draft.price) || 0,
  status: draft.status,
  row: draft,
});
